// Customer review API client for the PDP "What families are saying" block.
// Talks to the public shop endpoints (no auth). Helpful votes are anonymous and
// keyed by a locally-generated voterId persisted in localStorage.

const backendApiUrl: string = process.env.REACT_APP_BACKEND_API_URL!
// Reviews are served under /api/reviews/ (a dynamic CloudFront behaviour) rather
// than /api/shop/ — the shop behaviour is cache-optimized and strips query strings
// from the cache key (breaking paginated load-more + sort) and blocks POST/DELETE
// at the edge (breaking helpful votes).
const reviewsBase = `${backendApiUrl.replace(/\/$/, '')}/reviews`

export type ReviewSort = 'most_helpful' | 'newest' | 'highest' | 'lowest'

export interface ReviewSummary {
    productId: string
    average: number
    total: number
    distribution: Record<'1' | '2' | '3' | '4' | '5', number>
    wouldBuildAgainPct: number | null
}

export interface Review {
    id: string
    rating: number | null
    title: string | null
    body: string
    authorName: string
    authorPhotoUrl: string | null
    colorway: string | null
    verifiedBuild: boolean
    helpfulCount: number
    photos: string[]
    createdAt: string | null
    reply: { text: string; repliedAt: string | null } | null
}

export interface ReviewListResponse {
    items: Review[]
    nextCursor: string | null
    total: number
}

export interface ReviewListParams {
    sort?: ReviewSort
    rating?: number
    withPhotos?: boolean
    cursor?: string | null
    limit?: number
}

const VOTER_ID_KEY = 'kibu_review_voter_id'

/** Stable anonymous id for helpful voting; created once and persisted locally. */
export const getVoterId = (): string => {
    try {
        let voterId = localStorage.getItem(VOTER_ID_KEY)
        if (!voterId) {
            voterId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
                ? crypto.randomUUID()
                : `v_${Date.now()}_${Math.random().toString(36).slice(2)}`
            localStorage.setItem(VOTER_ID_KEY, voterId)
        }
        return voterId
    } catch {
        return `v_${Date.now()}_${Math.random().toString(36).slice(2)}`
    }
}

const headers = (region?: string): HeadersInit => ({
    'Content-Type': 'application/json',
    ...(region ? { 'x-country-code': region.toUpperCase() } : {}),
})

export const fetchReviewSummary = async (
    productId: string,
    region?: string,
): Promise<ReviewSummary> => {
    const response = await fetch(`${reviewsBase}/products/${productId}/summary/`, {
        method: 'GET',
        headers: headers(region),
    })
    if (!response.ok) {
        throw new Error(`Failed to load review summary (${response.status})`)
    }
    return response.json()
}

export const fetchReviews = async (
    productId: string,
    params: ReviewListParams = {},
    region?: string,
): Promise<ReviewListResponse> => {
    const query = new URLSearchParams()
    if (params.sort) query.set('sort', params.sort)
    if (typeof params.rating === 'number') query.set('rating', String(params.rating))
    if (params.withPhotos) query.set('withPhotos', 'true')
    if (params.cursor) query.set('cursor', params.cursor)
    if (params.limit) query.set('limit', String(params.limit))

    const suffix = query.toString() ? `?${query.toString()}` : ''
    const response = await fetch(`${reviewsBase}/products/${productId}/${suffix}`, {
        method: 'GET',
        headers: headers(region),
    })
    if (!response.ok) {
        throw new Error(`Failed to load reviews (${response.status})`)
    }
    return response.json()
}

export const setReviewHelpful = async (
    reviewId: string,
    helpful: boolean,
): Promise<{ helpfulCount: number; voted: boolean }> => {
    const response = await fetch(`${reviewsBase}/${reviewId}/helpful/`, {
        method: helpful ? 'POST' : 'DELETE',
        headers: headers(),
        body: JSON.stringify({ voterId: getVoterId() }),
    })
    if (!response.ok) {
        throw new Error(`Failed to record helpful vote (${response.status})`)
    }
    return response.json()
}
